import { PieLabelRenderProps } from 'recharts';

/**
 * Outside-label renderer for recharts `<Pie>` that prevents labels from
 * overlapping each other. Recharts' default label positions text radially at
 * each slice's mid-angle, so two adjacent thin slices (e.g. "Resolved 1%" and
 * "Pending 0%") end up at almost the same point and their text collides.
 *
 * This renderer keeps every label but pushes colliding ones apart vertically
 * and draws its own leader line to the slice. Use with `labelLine={false}` on
 * the `<Pie>` (we render the connector ourselves).
 */

const RADIAN = Math.PI / 180;
const MIN_GAP = 16; // px — a little more than the 12px font so lines never touch

/**
 * Given the y-positions already placed on one side of the chart and a new
 * label's natural y, return a y that is at least `minGap` away from all of
 * them, pushing in `pushDir` (+1 down, -1 up). Pure + deterministic so it can
 * be unit-tested without rendering.
 */
export function deconflictLabelY(
  placed: number[],
  naturalY: number,
  pushDir: 1 | -1,
  minGap: number = MIN_GAP,
): number {
  let y = naturalY;
  let guard = 0;
  while (placed.some((p) => Math.abs(p - y) < minGap) && guard < 64) {
    y += pushDir * minGap;
    guard++;
  }
  return y;
}

export interface PieLabelOptions {
  /** Separator between the name and the percentage. Defaults to a space. */
  separator?: string;
}

/**
 * Build a label render function for a single `<Pie>`. Each pie gets its own
 * renderer instance so the per-render collision state never leaks between
 * charts. State resets on the `index === 0` slice at the start of every render
 * pass (recharts renders slices in data order).
 */
export function makePieLabelRenderer(options: PieLabelOptions = {}) {
  const separator = options.separator ?? ' ';
  const placed: { left: number[]; right: number[] } = { left: [], right: [] };

  return function renderPieLabel(props: PieLabelRenderProps) {
    const { cx, cy, midAngle, outerRadius, percent, index } = props;

    if (
      typeof cx !== 'number' ||
      typeof cy !== 'number' ||
      typeof midAngle !== 'number' ||
      typeof outerRadius !== 'number'
    ) {
      return null;
    }

    // Reset per-render collision state at the first slice.
    if (index === 0) {
      placed.left = [];
      placed.right = [];
    }

    // A zero-size slice has no arc — nothing to label. Guard on `percent`
    // (always supplied, and exactly 0 for an empty slice) rather than the
    // dataKey value, so this works for any dataKey name (e.g. "tickets").
    const pct = typeof percent === 'number' ? percent : 0;
    if (pct <= 0) return null;
    const angle = -midAngle * RADIAN;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const isRight = cos >= 0;
    const side = isRight ? 'right' : 'left';

    // Point on the slice's outer edge (where the leader line starts).
    const sx = cx + outerRadius * cos;
    const sy = cy + outerRadius * sin;

    // Elbow just outside the ring, then a short horizontal run to the text.
    const elbowR = outerRadius + 16;
    const ex = cx + elbowR * cos;
    const naturalY = cy + elbowR * sin;

    // Push colliding labels away from the horizontal centre so clusters spread.
    const pushDir: 1 | -1 = sin >= 0 ? 1 : -1;
    const ey = deconflictLabelY(placed[side], naturalY, pushDir);
    placed[side].push(ey);

    const labelX = isRight ? ex + 14 : ex - 14;
    const color =
      (props as { fill?: string }).fill ||
      (props as { payload?: { color?: string; fill?: string } }).payload?.color ||
      (props as { payload?: { color?: string; fill?: string } }).payload?.fill ||
      'currentColor';

    const text = `${(props as { name?: string }).name ?? ''}${separator}${(pct * 100).toFixed(0)}%`;

    return (
      <g>
        <polyline
          points={`${sx},${sy} ${ex},${ey} ${labelX},${ey}`}
          stroke={color}
          strokeOpacity={0.6}
          strokeWidth={1}
          fill="none"
        />
        <text
          x={isRight ? labelX + 2 : labelX - 2}
          y={ey}
          textAnchor={isRight ? 'start' : 'end'}
          dominantBaseline="central"
          fill={color}
          fontSize={12}
          fontWeight={500}
        >
          {text}
        </text>
      </g>
    );
  };
}
